import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type {
    AddProductsToCartOutput as AddProductsToCartOutputRaw,
    Cart,
    CartItemInput,
    ProductInterface
} from '@pylot-data/pylotschema'
import { addToCartMutation } from './graphql/addToCartMutation'
import { CartData, useCart } from './use-cart'
import { getIsSignedIn } from './utils/getIsSignedIn'
import { getOptimisticAddToCartData } from './utils/getOptimisticAddToCartData'
import { useUI } from '@corratech/pylot-ui/context'
import { CartItemInterface, Maybe } from '@pylot-data/pylotschema'

export interface AddProductsToCartInput {
    cartId: string
    cartItems: CartItemInput[]
    isSignedIn: boolean
}

export interface AddProductsToCartOutput {
    addProductsToCart: AddProductsToCartOutputRaw
}

export interface UseAddToCartReturn {
    addToCart: (
        cartItems: CartItemInput[],
        products?: ProductInterface[],
        excludeItems?: string[]
    ) => Promise<CartData | undefined>
    isAdding: boolean
}

const formatData = (
    output: GraphqlFetchOutput<AddProductsToCartOutput>
): ((oldData?: CartData) => CartData) | CartData => {
    const outputData = output.data.addProductsToCart
    const errors = output.errors

    if (errors?.length || outputData?.user_errors?.length) {
        // populate cart data using the old data and append it with new errors
        return (oldData) => ({
            errors: errors,
            data: oldData?.data ?? outputData,
            user_errors: outputData?.user_errors
        })
    }

    return {
        data: { cart: outputData.cart },
        errors: errors
    }
}

// During 'editing cart item' we remove an old item and add a new item simultaneously
// So, we have to ignore the outdated cart item
const filterExcluded = (cart: Cart, excludeItems?: string[]): Cart => {
    if (cart.items && excludeItems) {
        cart.items = cart.items.filter(
            (item) => item && !excludeItems.includes(item.uid)
        )
    }
    return cart
}

export const useAddToCart = (): UseAddToCartReturn => {
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const { openSidebar } = useUI()
    const [isAdding, setIsAdding] = useState(false)
    const [optimisticItems, setOptimisticItems] = useState<
        Maybe<Array<Maybe<CartItemInterface>>>
    >()

    // If cart data is being updated during ATC request - restore optimistic items
    useEffect(() => {
        if (isAdding && data && optimisticItems) {
            data.data.cart.items = optimisticItems
            mutate(data, false)
        }
    }, [data])

    return {
        addToCart: async (
            cartItems: CartItemInput[],
            products?: ProductInterface[],
            excludeItems?: string[]
        ) => {
            setIsAdding(true)
            if (products) {
                const optimisticData = getOptimisticAddToCartData(
                    data!,
                    cartItems,
                    products
                )
                optimisticData.data.cart = filterExcluded(
                    optimisticData.data.cart,
                    excludeItems
                )
                setOptimisticItems(optimisticData.data.cart.items)
                mutate(optimisticData, false)
                openSidebar()
            }

            const response = await graphqlFetch<
                AddProductsToCartInput,
                AddProductsToCartOutput
            >({
                query: addToCartMutation,
                variables: {
                    cartId: String(data!.data.cart.id),
                    cartItems,
                    isSignedIn: getIsSignedIn()
                },
                locale
            })

            if (response.data.addProductsToCart.cart.items) {
                response.data.addProductsToCart.cart = filterExcluded(
                    response.data.addProductsToCart.cart,
                    excludeItems
                )
            }

            setOptimisticItems(null)
            const result = await mutate(formatData(response), false)
            // If there were errors - revalidate cart data
            if (
                result?.errors?.length ||
                response.data.addProductsToCart?.user_errors?.length
            ) {
                await mutate(undefined, true)
            }
            setIsAdding(false)
            return result
        },
        isAdding
    }
}
